import { Button } from '@/components/ui/button';
import { Shield, Lock, Eye, Trash2, ArrowLeft, Check, AlertTriangle, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { toast } from 'sonner';

export default function SecurityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleDeleteData = async () => {
    if (!user) {
      toast.error('Você precisa estar logado para apagar seus dados');
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja apagar todos os seus dados? Esta ação não pode ser desfeita.'
    );

    if (confirmed) {
      try {
        // This would be implemented in the backend
        toast.success('Solicitação de exclusão enviada. Seus dados serão removidos em até 24 horas.');
      } catch (error) {
        toast.error('Erro ao processar solicitação. Tente novamente.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-zinc-600 hover:text-zinc-900 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-100 mb-6">
            <Zap className="w-8 h-8 text-sky-600" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-zinc-900 mb-4">
            Segurança e Privacidade
          </h1>
          <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
            Sua privacidade é nossa prioridade. Entenda como o Zapt Resumo protege seus dados.
          </p>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-12">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">
                Importante: Não fazemos varredura automática
              </h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                O Zapt Resumo não acessa, monitora ou escaneia suas conversas automaticamente. 
                Você decide exatamente o que importar e quando. Nunca acessamos interfaces web de mensageria, 
                não usamos scraping, e não utilizamos APIs não-oficiais.
              </p>
              <p className="text-amber-700 text-sm leading-relaxed mt-3">
                Este aplicativo não é afiliado, associado ou endossado pelo WhatsApp LLC.
              </p>
            </div>
          </div>
        </div>

        {/* Security Features */}
        <div className="space-y-8 mb-16">
          <h2 className="font-heading text-2xl font-bold text-zinc-900">Como Protegemos Seus Dados</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Lock,
                title: 'Criptografia',
                description: 'Todos os dados são criptografados em trânsito (TLS) e em repouso (AES-256).'
              },
              {
                icon: Eye,
                title: 'Você no Controle',
                description: 'Você decide o que importar. Pode visualizar, exportar e deletar seus dados a qualquer momento.'
              },
              {
                icon: Shield,
                title: 'Conformidade LGPD',
                description: 'Seguimos todas as diretrizes da Lei Geral de Proteção de Dados do Brasil.'
              },
              {
                icon: Trash2,
                title: 'Direito ao Esquecimento',
                description: 'Solicite a exclusão total dos seus dados com um clique. Sem burocracia.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-zinc-50 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-violet-600" />
                </div>
                <h3 className="font-heading font-semibold text-zinc-900 mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sandbox Mode */}
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-8 mb-16">
          <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-4">Modo Sandbox</h2>
          <p className="text-zinc-600 mb-6 leading-relaxed">
            O modo Sandbox permite que você experimente todas as funcionalidades do Zapt Resumo 
            usando dados completamente simulados. Nenhuma conversa real é utilizada. É a forma 
            perfeita de conhecer a plataforma sem nenhum risco.
          </p>
          <ul className="space-y-3">
            {[
              'Dados 100% fictícios gerados pela plataforma',
              'Simula grupos, mensagens e interações reais',
              'Não requer nenhuma importação de dados pessoais',
              'Ideal para demonstrações e testes'
            ].map((item, index) => (
              <li key={index} className="flex items-center gap-3 text-zinc-700">
                <Check className="w-4 h-4 text-sky-500 flex-shrink-0" />
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* What We Don't Do */}
        <div className="mb-16">
          <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-6">O que NÃO fazemos</h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <ul className="space-y-3">
              {[
                'Não acessamos interfaces web privadas de apps de mensagem',
                'Não fazemos scraping de dados',
                'Não listamos seus grupos automaticamente',
                'Não usamos bibliotecas não-oficiais como Baileys',
                'Não integramos com APIs não autorizadas',
                'Não vendemos ou compartilhamos seus dados'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-red-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Delete Data */}
        <div className="border border-zinc-200 rounded-xl p-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-zinc-900 mb-4">
            Apagar Meus Dados
          </h2>
          <p className="text-zinc-600 mb-6 max-w-lg mx-auto">
            Você tem o direito de solicitar a exclusão completa dos seus dados a qualquer momento.
          </p>
          <Button 
            variant="destructive"
            className="rounded-full px-6"
            onClick={handleDeleteData}
            data-testid="delete-data-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Solicitar Exclusão de Dados
          </Button>
          <p className="text-xs text-zinc-500 mt-4">
            {user ? 'Seus dados serão removidos em até 24 horas após a solicitação.' : 'Faça login para solicitar a exclusão.'}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-zinc-500">
          <p>© 2026 Openova. Todos os direitos reservados.</p>
          <p className="mt-2">Dúvidas? Entre em contato: contato@openova.com.br</p>
        </div>
      </footer>
    </div>
  );
}
